import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw, Settings, Eye, EyeOff, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getOptionalCategories,
  createOptionalCategory,
  updateOptionalCategory,
  toggleCategoryActive,
  deleteOptionalCategory,
  getCategoryWithOptions
} from '../api/categories';
import OptionalCategoryModal from '../components/OptionalCategoryModal';
import CategoryOptionsModal from '../components/CategoryOptionsModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const OptionalCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await getOptionalCategories(showInactive);
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [showInactive]);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const filtered = categories.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  // Handle create/update
  const handleSubmit = async (data) => {
    try {
      if (selectedCategory) {
        await updateOptionalCategory(selectedCategory.id, data);
        toast.success('Category updated successfully');
      } else {
        await createOptionalCategory(data);
        toast.success('Category created successfully');
      }
      fetchCategories();
      setShowModal(false);
      setSelectedCategory(null);
    } catch (error) {
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (category) => {
    setSelectedCategory(category);
    setShowModal(true);
  };

  // Handle manage options
  const handleManageOptions = async (category) => {
    try {
      const data = await getCategoryWithOptions(category.id);
      setSelectedCategory(data);
      setShowOptionsModal(true);
    } catch (error) {
      toast.error('Failed to load category options');
    }
  };

  // Handle toggle active
  const handleToggleActive = async (category) => {
    try {
      await toggleCategoryActive(category.id);
      toast.success(`Category ${category.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to toggle category status');
    }
  };

  // Handle delete
  const handleDelete = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteOptionalCategory(selectedCategory.id);
      toast.success('Category deleted successfully');
      fetchCategories();
      setShowDeleteModal(false);
      setSelectedCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete category');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-black">Optional Categories</h1>
              <p className="text-gray-600 mt-1">
                Create custom categories for additional product attributes
              </p>
            </div>
            <button
              onClick={() => {
                setSelectedCategory(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Create Category
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowInactive(!showInactive)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showInactive
                  ? 'bg-accent-gold border-accent-gold text-primary-black'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              {showInactive ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              {showInactive ? 'All' : 'Active Only'}
            </button>
            <button
              onClick={fetchCategories}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-accent-gold">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Categories</h3>
            <p className="text-3xl font-bold text-primary-black">{categories.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-green-500">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Active</h3>
            <p className="text-3xl font-bold text-green-600">
              {categories.filter((c) => c.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-red-500">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Inactive</h3>
            <p className="text-3xl font-bold text-red-600">
              {categories.filter((c) => !c.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Showing</h3>
            <p className="text-3xl font-bold text-primary-black">{filteredCategories.length}</p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No categories found matching your search'
                : 'No optional categories yet. Create your first custom category!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className={`bg-white rounded-lg shadow-md border-2 hover:shadow-lg transition-shadow ${
                  category.is_active ? 'border-gray-200' : 'border-red-200 opacity-75'
                }`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-primary-black">
                          {category.name}
                        </h3>
                        {!category.is_active && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="capitalize">{category.input_type}</span>
                        <span>Order: {category.display_order}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleManageOptions(category)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      Options
                    </button>
                    <button
                      onClick={() => handleEdit(category)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleActive(category)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title={category.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {category.is_active ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(category)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <OptionalCategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedCategory(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedCategory}
      />

      <CategoryOptionsModal
        isOpen={showOptionsModal}
        onClose={() => {
          setShowOptionsModal(false);
          setSelectedCategory(null);
        }}
        category={selectedCategory}
        onUpdate={fetchCategories}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCategory(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedCategory?.name}
        loading={deleteLoading}
      />
    </div>
  );
};

export default OptionalCategoriesPage;
